<template>
    <div class="ticket-quiz-launcher" :class="{ 'is-minimized': minimized }">
        <transition name="ticket-quiz-launcher-fade" mode="out-in">
            <!-- Expanded card -->
            <div
                v-if="!minimized"
                key="card"
                class="ticket-quiz-launcher__shell"
            >
                <section
                    class="ticket-quiz-launcher__card"
                    role="complementary"
                    :aria-label="launcher.title"
                >
                    <button
                        type="button"
                        class="ticket-quiz-launcher__minimize"
                        :aria-label="launcher.minimize"
                        :title="launcher.minimize"
                        @click="minimize"
                    >
                        <svg
                            viewBox="0 0 16 16"
                            width="14"
                            height="14"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M3.5 8h9"
                                stroke="currentColor"
                                stroke-width="1.4"
                                stroke-linecap="round"
                            />
                        </svg>
                    </button>

                    <span class="ticket-quiz-launcher__eyebrow">
                        {{ launcher.eyebrow }}
                    </span>
                    <h3 class="ticket-quiz-launcher__title">
                        {{ launcher.title }}
                    </h3>
                    <p class="ticket-quiz-launcher__body">
                        {{ launcher.body }}
                    </p>
                    <button
                        type="button"
                        class="ticket-quiz-launcher__cta"
                        @click="$emit('open')"
                    >
                        <span class="ticket-quiz-launcher__cta-label">{{ launcher.cta }}</span>
                        <span class="ticket-quiz-launcher__cta-icon" aria-hidden="true">
                            <svg viewBox="0 0 20 20" width="12" height="12" fill="none">
                                <path d="M6 14L14 6M8 6h6v6" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </span>
                    </button>
                </section>
            </div>

            <!-- Minimized pill -->
            <button
                v-else
                key="pill"
                type="button"
                class="ticket-quiz-launcher__pill"
                :aria-label="pillLabel"
                @click="$emit('open')"
            >
                <span class="ticket-quiz-launcher__pill-dot" aria-hidden="true" />
                <span class="ticket-quiz-launcher__pill-text">
                    {{ pillLabel }}
                </span>
            </button>
        </transition>
    </div>
</template>

<script>
export default {
    name: "TicketQuizLauncher",
    props: {
        trans: {
            type: Object,
            required: false,
            default() {
                return {};
            }
        },
        storageKey: {
            type: String,
            required: false,
            default: "ticket_quiz_launcher_minimized"
        }
    },
    data() {
        return {
            minimized: false
        };
    },
    computed: {
        launcher() {
            const l = (this.trans && this.trans.launcher) || {};
            return {
                eyebrow: l.eyebrow || "",
                title: l.title || "",
                body: l.body || "",
                cta: l.cta || "",
                minimize: l.minimize || "",
                pill: l.pill || ""
            };
        },
        pillLabel() {
            // Fall back to the CTA when no dedicated pill label is provided.
            return this.launcher.pill || this.launcher.cta;
        }
    },
    mounted() {
        this.minimized = this.readStored();
        if (!this.minimized) {
            this.$emit("shown");
        }
    },
    methods: {
        readStored() {
            try {
                return window.localStorage.getItem(this.storageKey) === "1";
            } catch (e) {
                return false;
            }
        },
        writeStored() {
            try {
                window.localStorage.setItem(this.storageKey, "1");
            } catch (e) {
                // Storage unavailable (private mode / disabled): state stays in-memory.
            }
        },
        minimize() {
            this.minimized = true;
            this.writeStored();
            this.$emit("minimized");
        }
    }
};
</script>

<style lang="scss" scoped>
$launcher-purple: #7b2bff;
$launcher-purple-deep: #6f00e8;
$launcher-purple-soft: #f3ecff;
$launcher-text: #ffffff;
$launcher-muted: rgba(255, 255, 255, 0.82);
$launcher-hairline: rgba(255, 255, 255, 0.22);
$ease-launcher: cubic-bezier(0.32, 0.72, 0, 1);

.ticket-quiz-launcher {
    position: fixed;
    right: 20px;
    bottom: 68px;
    z-index: 999;
    max-width: calc(100vw - 40px);

    @media (min-width: 700px) {
        bottom: 20px;
    }

    &__shell {
        width: 336px;
        max-width: 100%;
        border-radius: 26px;
        box-shadow: 0 24px 60px rgba(67, 14, 151, 0.28);
    }

    &__card {
        position: relative;
        width: 100%;
        padding: 20px 20px 18px;
        border-radius: 25px;
        overflow: hidden;
        background: $launcher-purple;
        color: $launcher-text;
        text-align: left;
    }

    &__minimize {
        position: absolute;
        top: 18px;
        right: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        padding: 0;
        border: 1px solid $launcher-hairline;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.14);
        color: $launcher-text;
        cursor: pointer;
        appearance: none;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
        transition: transform 0.3s $ease-launcher, opacity 0.3s $ease-launcher;

        &:hover {
            transform: translateY(-1px);
        }

        &:focus {
            outline: none;
            box-shadow:
                inset 0 1px 0 rgba(255, 255, 255, 0.18),
                0 0 0 3px rgba(255, 255, 255, 0.2);
        }
    }

    &__eyebrow {
        display: inline-flex;
        align-items: center;
        min-height: 30px;
        max-width: calc(100% - 42px);
        padding: 0 12px;
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.22);
        background: rgba(255, 255, 255, 0.12);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        line-height: 1.2;
        color: rgba(255, 255, 255, 0.92);
    }

    &__title {
        margin: 14px 0 8px;
        max-width: 260px;
        font-size: 24px;
        font-weight: 700;
        line-height: 1.08;
        letter-spacing: -0.03em;
        color: $launcher-text;
    }

    &__body {
        margin: 0 0 16px;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.55;
        color: $launcher-muted;
    }

    &__cta {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        min-height: 48px;
        padding: 4px 4px 4px 18px;
        border: 0;
        border-radius: 999px;
        background: #fff;
        color: $launcher-purple-deep;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.2;
        cursor: pointer;
        appearance: none;
        box-shadow: 0 16px 28px rgba(67, 14, 151, 0.18);
        transition: transform 0.3s $ease-launcher, opacity 0.3s $ease-launcher;

        &:hover {
            transform: translateY(-1px);
        }

        &:active {
            transform: scale(0.98);
        }

        &:focus {
            outline: none;
            box-shadow:
                0 0 0 3px rgba(255, 255, 255, 0.22),
                0 16px 28px rgba(67, 14, 151, 0.18);
        }
    }

    &__cta-label {
        display: inline-flex;
        align-items: center;
        min-width: 0;
    }

    &__cta-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: rgba(123, 43, 255, 0.12);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
        transition: transform 0.3s $ease-launcher, opacity 0.3s $ease-launcher;
    }

    &__cta:hover &__cta-icon {
        transform: translateX(1px) translateY(-1px);
    }

    &__pill {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        padding: 11px 16px;
        border: 1px solid rgba(255, 255, 255, 0.22);
        border-radius: 999px;
        background: $launcher-purple;
        box-shadow: 0 20px 42px rgba(67, 14, 151, 0.26);
        color: $launcher-text;
        font-size: 13px;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        appearance: none;
        transition: transform 0.3s $ease-launcher, opacity 0.3s $ease-launcher;

        &:hover {
            transform: translateY(-1px);
        }

        &:active {
            transform: scale(0.97);
        }

        &:focus {
            outline: none;
            box-shadow:
                0 0 0 3px rgba(255, 255, 255, 0.2),
                0 20px 42px rgba(67, 14, 151, 0.26);
        }
    }

    &__pill-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 0 12px rgba(255, 255, 255, 0.32);
    }

    &__pill-text {
        color: $launcher-text;
    }
}

.ticket-quiz-launcher-fade-enter-active,
.ticket-quiz-launcher-fade-leave-active {
    transition: opacity 0.28s $ease-launcher, transform 0.28s $ease-launcher;
}

.ticket-quiz-launcher-fade-enter,
.ticket-quiz-launcher-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}
</style>
